// Must run before anything else evaluates (incl. pdfjs on the main thread).
import './polyfills';

import { LucideProvider } from 'lucide-react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { TooltipProvider } from './components/ui/tooltip';
import ApplicationError from './components/global/ApplicationError/ApplicationError';
import ErrorBoundary from './components/global/ErrorBoundary/ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import { initErrorReporter } from './services/errorReporter';

import './i18n';

import './fonts/fonts.css';
import './styles/globals.css';

initErrorReporter();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element with id "root" was not found.');
}

const root = createRoot(rootElement);

root.render(
  // No MUI ThemeProvider: the only screens still importing MUI are the static legal
  // pages, and they use `Box`/`Grid` without ever reading the theme. Dropping it took the
  // library out of the entry bundle — it now loads only on those lazy routes.
  <ErrorBoundary fallback={<ApplicationError />}>
    <BrowserRouter>
      {/* Radix's tooltip provider context has no default, so `Tooltip` throws without an
            ancestor. One at the root beats one per feature — and its `delayDuration={0}`
            default is the design system's (§5: tooltips are a 180ms fade, not a delayed reveal). */}
      <TooltipProvider>
        {/* The design system's icon defaults (§5: stroke 1.5, 20px) in one place, so call
              sites carry a size only when they mean something other than 20 — 16 for dense
              UI, 18 for the toolbar. lucide's own defaults (24 / stroke 2) are heavier than
              anything the design allows, so leaving them in force was never an option.
              A `size-*` class still wins over the size set here: CSS beats the attribute. */}
        <LucideProvider size={20} strokeWidth={1.5}>
          <App />
        </LucideProvider>
      </TooltipProvider>
    </BrowserRouter>
  </ErrorBoundary>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
